<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTicketContactPersonService } from 'src/services/ticket-contact-person-service';
import { CreateTicketContactPersonDTOReference } from 'src/api/models';

const props = defineProps<{
  initialEntity?: Record<string, any>;
}>();

const emit = defineEmits<{
  created: [contact: Record<string, any>];
  cancel: [];
}>();

const { createTicketContactPerson } = useTicketContactPersonService();

const firstName = ref(props.initialEntity?.first_name ?? '');
const lastName = ref(props.initialEntity?.last_name ?? '');
const phoneNumber = ref(props.initialEntity?.phone_number ?? '');
const mobilePhoneNumber = ref(props.initialEntity?.mobile_phone_number ?? '');
const email = ref(props.initialEntity?.email ?? '');
const isSubmitting = ref(false);

const isValid = computed(() =>
  !!(firstName.value.trim() || lastName.value.trim()),
);

function resetForm() {
  firstName.value = '';
  lastName.value = '';
  phoneNumber.value = '';
  mobilePhoneNumber.value = '';
  email.value = '';
}

async function handleSubmit() {
  if (!isValid.value) return;
  isSubmitting.value = true;
  try {
    const response = await createTicketContactPerson(
      {
        first_name: firstName.value || undefined,
        last_name: lastName.value || undefined,
        phone_number: phoneNumber.value || undefined,
        mobile_phone_number: mobilePhoneNumber.value || undefined,
        email: email.value || undefined,
      },
      CreateTicketContactPersonDTOReference.site,
    );
    if (response) {
      emit('created', typeof response === 'string' ? { id: response, first_name: firstName.value, last_name: lastName.value, phone_number: phoneNumber.value, mobile_phone_number: mobilePhoneNumber.value, email: email.value } : response as Record<string, any>);
      resetForm();
    }
  } catch (error) {
    console.error('Failed to create contact person:', error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="create-panel">
    <div class="flex items-center justify-between mb-2">
      <div class="entity-badge bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400">
        <i class="pi pi-user" style="font-size: 11px" />
        Nieuw contact locatie
      </div>
      <button class="cancel-link" @click="emit('cancel')">
        <i class="pi pi-arrow-left" style="font-size: 10px" />
        Search
      </button>
    </div>

    <div class="form-fields">
      <div class="flex gap-2">
        <input
          v-model="firstName"
          type="text"
          class="field flex-1"
          placeholder="First name *"
        />
        <input
          v-model="lastName"
          type="text"
          class="field flex-1"
          placeholder="Last name *"
        />
      </div>
      <input
        v-model="phoneNumber"
        type="tel"
        class="field"
        placeholder="Phone number"
      />
      <input
        v-model="mobilePhoneNumber"
        type="tel"
        class="field"
        placeholder="Mobile number"
      />
      <input
        v-model="email"
        type="email"
        class="field"
        placeholder="Email"
      />

      <button
        class="submit-btn"
        :disabled="!isValid || isSubmitting"
        @click="handleSubmit"
      >
        <i v-if="isSubmitting" class="pi pi-spinner pi-spin" style="font-size: 11px" />
        Toevoegen
      </button>
    </div>
  </div>
</template>

<style scoped>
.submit-btn {
  justify-content: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}
</style>
